
import { BtnPrimary } from '../ui/components';
import { state } from '../store';
import { t, getLocalized } from '../i18n';
import { sanitizeHtml } from '../utils/sanitize';
import { safeStorage } from '../utils/storage';
import { cardsManager } from '../engine/cardsManager';
import type { DifficultyLevel } from '../engine/cardsManager';

export interface Player {
  id: string;
  name: string;
  emoji: string;
}

const EMOJIS = ['😎', '🦊', '🐻', '🐱', '🐶', '🐯', '🦁', '🐵', '🐼', '🐨', '🦄', '🐸'];



function loadPlayers(): Player[] {
  try {
    const saved = safeStorage.get('np_players');
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length >= 2) return parsed;
    }
  } catch(e) {}
  // Default players — empty names to force the user to type their own
  return [
    { id: '1', name: '', emoji: '😎' },
    { id: '2', name: '', emoji: '🦊' },
  ];
}

function savePlayers(): void {
  try {
    safeStorage.set('np_players', JSON.stringify(currentPlayers));
  } catch(e) {}
}

let _saveTimeout: number | null = null;
function debouncedSavePlayers(): void {
  if (_saveTimeout !== null) clearTimeout(_saveTimeout);
  _saveTimeout = window.setTimeout(() => {
    _saveTimeout = null;
    savePlayers();
  }, 500);
}

export let currentPlayers: Player[] = loadPlayers();

let selectedGameId: string = 'tod';
let selectedLevelIndex: number = 0;

declare global {
  interface Window {
    openGameSetup: (gameId: string, levelIndex: number) => void;
    addPlayer: () => void;
    removePlayer: (id: string) => void;
    cyclePlayerEmoji: (id: string) => void;
    updatePlayerName: (id: string, name: string) => void;
    launchGameFromSetup: () => void;
    startEmojiGame: (title: string, words: string[]) => void;
    showToast: (msg: string, type?: 'success' | 'error' | 'info', durationMs?: number) => void;
    __showSessionExpired: () => void;
  }
}

function renderPlayerCard(p: Player, canRemove: boolean): string {
  return `
    <div 
      data-player-id="${p.id}"
      class="flex items-center gap-[12px] p-[12px_14px] bg-glass border border-b-glass rounded-md transition-all duration-150"
    >
      <button 
        id="emoji-btn-${p.id}"
        class="w-[44px] h-[44px] rounded-full bg-white/[0.08] flex items-center justify-center text-[22px] cursor-pointer shrink-0 border-none"
        data-action="cycle-player-emoji" data-player-id="${p.id}"
      >
        ${p.emoji}
      </button>
      <input 
        data-player-input="${p.id}"
        class="flex-1 bg-transparent border-none outline-none font-body text-[16px] font-bold text-t-primary placeholder-t-muted" 
        placeholder="${t('setup_player_placeholder')}" 
        value="${sanitizeHtml(p.name)}"
        data-input-action="update-player-name" data-player-id="${p.id}"
      >
      ${canRemove ? `<span 
        data-remove-btn="${p.id}"
        class="text-[20px] text-t-muted cursor-pointer p-[4px] transition-colors duration-150 hover:text-neon-pink"
        data-action="remove-player" data-player-id="${p.id}"
      >
        ✕
      </span>` : ''}
    </div>
  `;
}

/** Sync remove buttons: show when >2 players, hide when <=2 */
function updateRemoveButtons(): void {
  const canRemove = currentPlayers.length > 2;
  const container = document.getElementById('players-list');
  if (!container) return;

  // Remove all existing remove buttons
  container.querySelectorAll('[data-remove-btn]').forEach(el => el.remove());

  if (canRemove) {
    // Add remove buttons to each player card
    currentPlayers.forEach(p => {
      const card = container.querySelector(`[data-player-id="${p.id}"]`);
      if (card && !card.querySelector('[data-remove-btn]')) {
        const span = document.createElement('span');
        span.setAttribute('data-remove-btn', p.id);
        span.className = 'text-[20px] text-t-muted cursor-pointer p-[4px] transition-colors duration-150 hover:text-neon-pink';
        span.textContent = '✕';
        span.onclick = () => window.removePlayer(p.id);
        card.appendChild(span);
      }
    });
  }
}

window.openGameSetup = (gameId: string, levelIndex: number) => {
  selectedGameId = gameId;
  selectedLevelIndex = levelIndex;
  isLaunching = false;
  window.navTo('game-setup');
};

window.addPlayer = () => {
  if (currentPlayers.length >= 50) return;
  const newPlayer: Player = {
    id: Math.random().toString(36).substring(7),
    name: '',
    emoji: EMOJIS[Math.floor(Math.random() * EMOJIS.length)],
  };
  currentPlayers.push(newPlayer);
  savePlayers();

  // DOM-only update: insert new card before the "add player" button
  const container = document.getElementById('players-list');
  if (container) {
    const addBtn = container.querySelector('[data-add-player]');
    const temp = document.createElement('div');
    temp.innerHTML = renderPlayerCard(newPlayer, currentPlayers.length > 2).trim();
    const newCard = temp.firstElementChild!;
    if (addBtn) {
      container.insertBefore(newCard, addBtn);
    } else {
      container.appendChild(newCard);
    }
    updateRemoveButtons();
    // Focus the new input
    const input = newCard.querySelector<HTMLInputElement>('input');
    if (input) input.focus();
  } else {
    window.renderApp();
  }
};

window.removePlayer = (id: string) => {
  if (currentPlayers.length <= 2) return;
  currentPlayers = currentPlayers.filter(p => p.id !== id);
  savePlayers();

  // DOM-only update: remove the card
  const card = document.querySelector(`[data-player-id="${id}"]`);
  if (card) {
    card.remove();
    updateRemoveButtons();
  } else {
    window.renderApp();
  }
};

window.cyclePlayerEmoji = (id: string) => {
  const player = currentPlayers.find(p => p.id === id);
  if (player) {
    const currentIndex = EMOJIS.indexOf(player.emoji);
    const nextIndex = (currentIndex + 1) % EMOJIS.length;
    player.emoji = EMOJIS[nextIndex];
    savePlayers();
    
    // Direct DOM update to prevent losing focus on inputs while changing avatar
    const emojiBtn = document.getElementById(`emoji-btn-${id}`);
    if (emojiBtn) emojiBtn.innerHTML = player.emoji;
  }
};

window.updatePlayerName = (id: string, name: string) => {
  const player = currentPlayers.find(p => p.id === id);
  if (player) {
    player.name = name.slice(0, 30);
    debouncedSavePlayers();
  }
};

function showFirstTimeTutorial(gameId: string, gameIcon: string, gameName: string, rules: string, onConfirm: () => void): void {
  const seenKey = `pg_tutorial_seen_${gameId}`;
  if (safeStorage.get(seenKey)) {
    onConfirm();
    return;
  }

  const overlay = document.createElement('div');
  overlay.id = 'tutorial-overlay';
  overlay.className = 'fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-void/95 backdrop-blur-sm p-[24px]';
  overlay.innerHTML = `
    <div class="w-full max-w-[390px] flex flex-col items-center gap-[20px] animate-[fadeInUp_0.3s_ease]">
      <div class="text-[64px] leading-none">${sanitizeHtml(gameIcon)}</div>
      <div class="text-[22px] font-extrabold text-center">${sanitizeHtml(gameName)}</div>
      <div class="w-full p-[20px] bg-[linear-gradient(135deg,rgba(255,45,107,0.08),rgba(160,32,240,0.05))] border border-neon-pink/20 rounded-xl">
        <div class="text-[11px] font-extrabold tracking-[1.5px] text-neon-pink mb-[10px]">${t('setup_rules_label')}</div>
        <div class="text-[14px] font-semibold text-t-secondary leading-[1.7]">${sanitizeHtml(rules)}</div>
      </div>
      <button id="tutorial-confirm" class="w-full h-[52px] rounded-xl bg-gradient-to-r from-neon-pink to-neon-purple text-white font-extrabold text-[16px] tracking-wide shadow-[0_0_20px_rgba(255,45,107,0.4)] active:scale-[0.97] transition-transform">
        ${t('tutorial_got_it')}
      </button>
    </div>
  `;

  document.body.appendChild(overlay);

  document.getElementById('tutorial-confirm')?.addEventListener('click', () => {
    safeStorage.set(seenKey, '1');
    overlay.remove();
    onConfirm();
  });
}

let isLaunching = false;

window.launchGameFromSetup = async () => {
  if (isLaunching) return;
  isLaunching = true;
  // Проверка: у каждого игрока должно быть непустое имя (trim)
  const emptyPlayers = currentPlayers.filter(p => !p.name.trim());
  if (emptyPlayers.length > 0) {
    // Подсветить пустые поля — добавить класс к карточкам
    emptyPlayers.forEach(p => {
      const card = document.querySelector(`[data-player-id="${p.id}"]`);
      if (card) {
        card.classList.add('border-amber-400/60', 'shadow-[0_0_12px_rgba(251,191,36,0.3)]');
        card.classList.remove('border-white/[0.08]', 'border-b-glass');
        // Убрать подсветку через 2 сек
        setTimeout(() => {
          card.classList.remove('border-amber-400/60', 'shadow-[0_0_12px_rgba(251,191,36,0.3)]');
          card.classList.add('border-white/[0.08]', 'border-b-glass');
        }, 2000);
      }
      // Сфокусироваться на первом пустом input
      const input = document.querySelector<HTMLInputElement>(`[data-player-input="${p.id}"]`);
      if (input && emptyPlayers[0].id === p.id) {
        input.focus();
      }
    });
    window.showToast(t('setup_warning'), 'error');
    isLaunching = false;
    return; // STOP — не запускать игру
  }

  const game = state.games.find(g => g.id === selectedGameId);
  const level = state.levels[selectedGameId]?.[selectedLevelIndex];

  if (!game || !level) return;

  showFirstTimeTutorial(
    selectedGameId,
    game.icon,
    getLocalized(game.name),
    getLocalized(game.rules),
    async () => {
      const btn = document.getElementById('btn-launch');
      const originalHtml = btn ? btn.innerHTML : '';
      if (btn) {
        btn.style.pointerEvents = 'none';
        btn.innerHTML = `<span class="icon">⌛</span><span>${t('loading')}</span>`;
      }

      try {
        const lang = state.user?.lang || 'en';
        const levelId = level.id;
        const difficulty = (state.difficultyRange ?? null) as DifficultyLevel;

        await cardsManager.startSession(selectedGameId, levelId, lang, difficulty);

        const fullTitle = `${game.icon} ${getLocalized(game.name)}`;

        if (['wai', 'describe', 'act'].includes(selectedGameId)) {
          window.startTimerGame(fullTitle, [], selectedGameId);
          return;
        }

        if (selectedGameId === 'emoji') {
          window.startEmojiGame?.(fullTitle, []);
          return;
        }

        if (selectedGameId === 'tod') {
          window.startTextGame(fullTitle, {
            cards: [],
            card_type: 'tod',
            total: 0,
            page: 1,
            total_pages: 1,
            is_mock: false,
            session_seed: 0,
          }, selectedGameId);
        } else {
          window.startTextGame(fullTitle, [], selectedGameId);
        }
      } catch (err) {
        isLaunching = false;
        console.error('Failed to load cards:', err);
        const errMsg = err instanceof Error ? err.message : String(err);

        if (errMsg.includes('Network error') || errMsg.includes('banned') || errMsg.includes('halted')) {
          // Глобально обработано в request()
        } else if (errMsg.includes('401') || errMsg.includes('JWT') || errMsg.includes('Unauthorized') || errMsg.includes('session_expired')) {
          // Already handled by request() interceptor — no duplicate __showSessionExpired
        } else if (errMsg.includes('403')) {
          // Уровень заблокирован — toast error_access_denied уже показан в request()
        } else if (errMsg.includes('503')) {
          window.showToast(t('cards_unavailable'), 'error');
        } else {
          window.showToast(t('error_generic'), 'error');
        }

        if (btn) {
          btn.style.pointerEvents = 'auto';
          btn.innerHTML = originalHtml;
        }
      }
    }
  );
};

export function renderGameSetup(): string {
  const game = state.games.find(g => g.id === selectedGameId);
  const level = state.levels[selectedGameId]?.[selectedLevelIndex];

  if (!game || !level) {
    // Data was cleared (e.g. after unlock) — redirect to levels screen
    setTimeout(() => (window as any).navTo('games'), 0);
    return `<div class="w-full max-w-[430px] h-screen mx-auto bg-void flex items-center justify-center">
      <div class="text-center">
        <div class="text-[48px] animate-pulse mb-4">🎮</div>
        <div class="text-t-secondary text-[14px] font-semibold">Loading...</div>
      </div>
    </div>`;
  }

  const canRemove = currentPlayers.length > 2;

  const playersHtml = currentPlayers.map(p => renderPlayerCard(p, canRemove)).join('');

  return `
    <div class="absolute inset-0 overflow-y-auto overflow-x-hidden pb-[96px] z-10 scrollbar-none bg-void transform translate-y-0" id="screen-game-setup">
      <!-- Header -->
      <div class="p-[16px_16px_0] flex items-center gap-[12px]">
        <div class="w-[38px] h-[38px] bg-white/[0.07] border border-b-glass rounded-full flex items-center justify-center cursor-pointer text-[18px] transition-all duration-150 active:scale-95 active:bg-white/[0.12]" data-action="nav" data-to="games">←</div>
        <div>
          <div class="text-[11px] text-t-muted font-bold tracking-[0.5px]">${t('setup_mode')}</div>
          <div class="font-display text-[20px] font-extrabold flex items-center gap-[6px]">${game.icon} ${sanitizeHtml(getLocalized(game.name))}</div>
        </div>
      </div>

      <!-- How to play -->
      <div class="text-[12px] font-extrabold tracking-[1.5px] uppercase text-t-muted mb-[12px] px-[20px] mt-[20px]">${t('setup_how_to')}</div>
      <div class="m-[0_16px] p-[16px] bg-[linear-gradient(135deg,rgba(255,45,107,0.08),rgba(160,32,240,0.05))] border border-neon-pink/20 rounded-lg">
        <div class="text-[11px] font-extrabold tracking-[1.5px] text-neon-pink mb-[8px]">${t('setup_rules_label')}</div>
        <div class="text-[13px] font-semibold text-t-secondary leading-[1.6]">
          ${sanitizeHtml(getLocalized(game.rules))}
        </div>
      </div>

      <!-- Selected level -->
      <div class="text-[12px] font-extrabold tracking-[1.5px] uppercase text-t-muted mb-[12px] px-[20px] mt-[20px]">${t('setup_selected')}</div>
      <div class="px-[16px]">
        <div class="flex items-center gap-[10px] p-[12px_14px] bg-glass border border-b-glass rounded-md">
          <span class="text-[28px]">${level.icon}</span>
          <div class="flex-1">
            <div class="text-[15px] font-extrabold">${sanitizeHtml(getLocalized(level.name))}</div>
            <div class="text-[12px] text-t-secondary font-semibold">${sanitizeHtml(getLocalized(level.desc))}</div>
          </div>
          <div class="text-[13px] text-neon-pink font-extrabold cursor-pointer" data-action="nav" data-to="games">${t('setup_change')}</div>
        </div>
      </div>

      <!-- Difficulty -->
      <div class="text-[12px] font-extrabold tracking-[1.5px] uppercase text-t-muted mb-[12px] px-[20px] mt-[20px]">
        ${t('difficulty_title')}
      </div>
      <div class="px-[16px]">
        <div class="flex gap-2">
          ${(['easy', 'medium', 'hard', 'all'] as const).map(key => {
            const rangeValues: Record<string, string | null> = { easy: 'easy', medium: 'medium', hard: 'hard', all: null };
            const icons: Record<string, string> = { easy: '😊', medium: '🤔', hard: '🧠', all: '🎲' };
            const range = rangeValues[key];
            const isActive = state.difficultyRange === range;
            const activeClass = isActive ? 'bg-neon-pink/15 border-neon-pink' : 'bg-glass border-transparent';
            const textClass = isActive ? 'text-neon-pink' : 'text-t-muted';
            return `
              <div class="flex-1 flex flex-col items-center gap-1 p-[10px_8px] rounded-lg border cursor-pointer transition-all duration-150 active:scale-95 ${activeClass}"
                   data-action="set-difficulty" data-range="${range ?? ''}">
                <span class="text-[18px]">${icons[key]}</span>
                <span class="text-[10px] font-bold ${textClass}">${t('difficulty_' + key)}</span>
              </div>
            `;
          }).join('')}
        </div>

      </div>

      <!-- Players -->
      <div class="text-[12px] font-extrabold tracking-[1.5px] uppercase text-t-muted mb-[12px] px-[20px] mt-[20px]">
        ${t('setup_players')} <span class="text-t-muted text-[11px] font-normal">${t('setup_players_sub')}</span>
      </div>
      <div id="players-list" class="flex flex-col gap-[10px] px-[16px]">
        ${playersHtml}
        
        <!-- Add player button -->
        <div 
          data-add-player
          class="flex items-center justify-center gap-[12px] p-[12px_14px] bg-transparent border-[1.5px] border-dashed border-white/[0.12] rounded-md cursor-pointer transition-all duration-150 active:bg-white/[0.05]"
          data-action="add-player"
        >
          <span class="text-[20px] text-t-muted">+</span>
          <span class="text-[14px] font-bold text-t-muted ml-[8px]">${t('add_player')}</span>
        </div>
      </div>

      <!-- Launch button -->
      <div class="p-[24px_16px_32px]">
        ${BtnPrimary({
          id: 'btn-launch',
          text: t('setup_launch'),
          icon: '🎮',
          onClick: 'launch-game',
        })}
      </div>
    </div>
  `;
}
